import * as z from 'zod';
import { SideSchema } from './game-state';
import { HordeGameFeaturesSchema } from './horde-game-features';
import { IdSchema, TimestampSchema } from './horde-schema-primitives';
import { BattlefieldStateSchema, HordeStateSchema } from './horde-session-battlefield';
import {
    CorePhaseSchema,
    GameLifecycleSchema,
    RulesetReferenceSchema,
    RulesInterpretationConfigSchema,
    WorkflowCursorSchema,
} from './horde-session-core';
import {
    ActionInstanceSchema,
    EffectInstanceSchema,
    PromptSchema,
    PublicCardStateSchema,
} from './horde-session-effects';
import {
    MaterializedRandomResultSchema,
    PlayerSecretViewStateSchema,
    PublicPlayerStateSchema,
} from './horde-session-secrets';

export const PublicHistoryEntrySchema = z.object({
    actorUserId: IdSchema,
    at: TimestampSchema,
    eventId: IdSchema,
    eventType: z.string().min(1),
    revision: z.number().int().positive(),
    summary: z.string().min(1),
});
export type PublicHistoryEntry = z.infer<typeof PublicHistoryEntrySchema>;

export const SetupStateSchema = z.object({
    armyConstructionConfirmed: z.boolean(),
    deploymentId: IdSchema.nullable(),
    detachmentPoints: z.number().int().nonnegative(),
    gameMasterUserId: IdSchema.nullable(),
    gameSize: z.union([z.literal(1000), z.literal(2000)]).nullable(),
    hordeFactionIds: z.array(IdSchema),
    initialSpawnComplete: z.boolean(),
    playerDeploymentConfirmed: z.boolean(),
    playerListsConfirmed: z.boolean(),
    primaryFactionId: IdSchema.nullable(),
    primaryFactionRuleId: IdSchema.nullable(),
    roundMode: z.enum(['fiveRounds', 'infinite']).nullable(),
    spawnZoneGeometryConfirmed: z.boolean(),
    startOfGameAbilitiesConfirmed: z.boolean(),
    tableLayoutConfirmed: z.boolean(),
});
export type HordeSetupState = z.infer<typeof SetupStateSchema>;

export const RoundStateSchema = z.object({
    activeSide: SideSchema,
    battleRound: z.number().int().positive(),
    phase: CorePhaseSchema.nullable(),
    resupplyFinishedPlayerIds: z.array(IdSchema),
    spawnMarkedComplete: z.boolean(),
});
export type HordeRoundState = z.infer<typeof RoundStateSchema>;

export const PersistedHordeGameSchema = z
    .object({
        acceptedEventIds: z.array(IdSchema).max(512),
        actions: z.array(ActionInstanceSchema),
        battlefield: BattlefieldStateSchema,
        cards: PublicCardStateSchema,
        effects: z.array(EffectInstanceSchema),
        features: HordeGameFeaturesSchema.optional(),
        gameId: IdSchema,
        historySummary: z.array(PublicHistoryEntrySchema).max(512),
        horde: HordeStateSchema,
        host: z
            .object({
                deviceId: IdSchema,
                userId: IdSchema,
            })
            .strict(),
        lastCommittedAt: TimestampSchema,
        lifecycle: GameLifecycleSchema,
        name: z.string().min(1),
        players: z.array(PublicPlayerStateSchema).max(4),
        prompts: z.array(PromptSchema),
        randomResults: z.array(MaterializedRandomResultSchema),
        revision: z.number().int().nonnegative(),
        round: RoundStateSchema.nullable(),
        rulesInterpretations: RulesInterpretationConfigSchema,
        ruleset: RulesetReferenceSchema,
        schemaVersion: z.number().int().positive(),
        setup: SetupStateSchema,
        workflow: WorkflowCursorSchema,
    })
    .strict();
export type PersistedHordeGame = z.infer<typeof PersistedHordeGameSchema>;

export const ValidationProblemSchema = z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    path: z.array(z.union([z.string(), z.number()])),
});
export type ValidationProblem = z.infer<typeof ValidationProblemSchema>;

export const PendingIntentMetadataSchema = z.object({
    eventId: IdSchema,
    eventType: z.string().min(1),
    expectedRevision: z.number().int().nonnegative(),
    submittedAt: TimestampSchema,
});
export type PendingIntentMetadata = z.infer<typeof PendingIntentMetadataSchema>;

export const StoreRuntimeSchema = z.object({
    commit: z.enum(['idle', 'writing', 'conflict', 'failed']),
    hydration: z.enum(['loading', 'ready', 'failed']),
    lastObservedRevision: z.number().int().nonnegative(),
    pendingIntent: PendingIntentMetadataSchema.nullable(),
    replication: z.enum(['offline', 'connecting', 'live', 'error']),
    validationErrors: z.array(ValidationProblemSchema),
    viewer: z.object({
        deviceId: IdSchema,
        playerId: IdSchema.nullable(),
        role: z.enum(['host', 'player', 'observer', 'gameMaster']),
        userId: IdSchema,
    }),
});
export type StoreRuntime = z.infer<typeof StoreRuntimeSchema>;

export const HordeStoreContextSchema = z.object({
    game: PersistedHordeGameSchema,
    mySecretView: PlayerSecretViewStateSchema.nullable(),
    runtime: StoreRuntimeSchema,
});
export type HordeStoreContext = z.infer<typeof HordeStoreContextSchema>;

export const HordeStoreInputSchema = z.object({
    hydratedPlayerSecretView: PlayerSecretViewStateSchema.nullable(),
    hydratedPublicSnapshot: PersistedHordeGameSchema,
    runtime: StoreRuntimeSchema,
});
export type HordeStoreInput = z.infer<typeof HordeStoreInputSchema>;
